/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * localInteractions — offline, deterministic drug-drug interaction checker.
 *
 * Uses the local JSON dictionary (`src/data/drugInteractions.json`) with the
 * top 50 Italian active principles and 50+ curated interaction pairs.
 *
 * API:
 *   checkLocalInteractions(newDrugName, activeTherapy) → { findings: [{severity, reason, drugA, drugB}], count }
 *
 * Fuzzy matching: we normalize accents and lowercase before lookup, and match
 * on substring so "amoxicillina/acido clavulanico" also flags interactions
 * defined against "amoxicillina".
 */
import db from '@/data/drugInteractions.json';

const normalize = (s) => (s || '')
  .toString()
  .toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s\/-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Test whether a drug string "matches" a canonical principle. */
const matches = (candidate, canonical) => {
  const c = normalize(candidate);
  const k = normalize(canonical);
  if (!c || !k) return false;
  return c === k || c.includes(k) || k.includes(c);
};

/**
 * Given a `newDrug` (name string) and `activeTherapy` (array of active drug
 * name strings currently taken by the patient OR being prescribed), return
 * an array of interaction findings sorted by severity descending.
 */
export const checkLocalInteractions = (newDrug, activeTherapy = []) => {
  const cand = normalize(newDrug);
  if (!cand) return { findings: [], count: 0 };

  const findings = [];
  const seen = new Set();
  for (const other of activeTherapy) {
    const otherN = normalize(other);
    if (!otherN || otherN === cand) continue;
    for (const rule of db.interactions) {
      const hitA = (matches(cand, rule.a) && matches(otherN, rule.b)) ||
                   (matches(cand, rule.b) && matches(otherN, rule.a));
      if (!hitA) continue;
      const key = [rule.a, rule.b, other].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      findings.push({
        drugA: newDrug,
        drugB: other,
        severity: rule.severity,
        reason: rule.reason,
      });
    }
  }

  const sev = { critical: 3, high: 2, medium: 1, low: 0 };
  findings.sort((x, y) => (sev[y.severity] || 0) - (sev[x.severity] || 0));
  return { findings, count: findings.length };
};

/** Check ALL pairs across a full prescription list (for one-shot validation). */
export const checkAllPairs = (drugList = []) => {
  const out = { findings: [], count: 0 };
  for (let i = 0; i < drugList.length; i++) {
    for (let j = i + 1; j < drugList.length; j++) {
      const r = checkLocalInteractions(drugList[i], [drugList[j]]);
      out.findings.push(...r.findings);
    }
  }
  const sev = { critical: 3, high: 2, medium: 1, low: 0 };
  out.findings.sort((x, y) => (sev[y.severity] || 0) - (sev[x.severity] || 0));
  out.count = out.findings.length;
  return out;
};

/** Metadata about the dictionary version — used by an "info" pill in the UI. */
export const dictionaryInfo = {
  version: db.version,
  updatedAt: db.updated_at,
  principlesCount: (db.activePrinciples || []).length,
  rulesCount: (db.interactions || []).length,
};

export default checkLocalInteractions;
